import { Box, Button, Chip, CircularProgress, Stack } from "@mui/material";
import React, { useRef, useState } from "react";
import { FileUploadClient } from "../../../api/FileUploadClient";
import IconComponent from "../../../common/components/IconComponent";
import { useApiClient, useErrorHandler } from "../../../common/hooks";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { InputFileModel } from "../../Models/InputFileModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const InputFileComponent: React.FC<IComponentProps<InputFileModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const fileClient = useApiClient(FileUploadClient);

  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const label = useObservedText(component.Label, context, "InputFileComponent");
  const [attributeValue] = useObservedExpression<string>(component.AttributeValue, context, "InputFileComponent");

  const componentLayoutSx = getComponentLayout(component);
  const selectionProps = useComponentSelection({ component, componentPath });

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleClear = () => {
    setFileName(undefined);
    if (inputRef.current) inputRef.current.value = "";
    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, null);
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await fileClient.uploadFile(component.Id, file, component.AttributeKey, attributeValue ?? undefined);

      if (result?.id && component.Value) {
        context.Evaluator.assign(component.Value, context, result.id);
        setFileName(file.name);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={componentLayoutSx} {...selectionProps}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          onClick={handleClick}
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={14} /> : <IconComponent iconName="UploadFile" />}
        >
          {label ?? "Upload File"}
        </Button>
        {fileName && <Chip label={fileName} size="small" onDelete={handleClear} />}
      </Stack>
      <input ref={inputRef} type="file" accept={component.Accept} style={{ display: "none" }} onChange={handleFileChange} />
    </Box>
  );
};

export default InputFileComponent;
